// IE 2026 09 26 / ODOO-… (заказ Игоря: расшифровка кусочка графика) — чистая логика формы расшифровки точки / блока графика (без Vue, стора и wijmo):
// точка по серии и строке графика, настройки формы (viewParams.<chart>.drill-dims / drill-top / click-drill), строки блоков (доли, полосы), числа,
// разбиение серии в записи виджета (doc.viewList: series_dimension / series_limit меры + колонка измерения) и откат. Проверка —
// handoff-files/chart-drill-2026-09-26/chartDrill.test.mjs (node). Компонент формы — components/widgets/ChartDrillBlocks.vue, связка с графиком —
// utils/chartDrillMixin.js (AttrChart), бэк — метод виджета chart_drill (common_modules/chart_drill.py).

export const DRILL_TOP_DEFAULT = 5
export const DRILL_TOP_CHOICES = [3, 5, 10]
export const DRILL_OTHERS_KEY = '__others__'

// топ значений: число 1…50, иначе умолчание
export function drillTop(v) {
    const n = Math.round(Number(v))
    return Number.isFinite(n) && n >= 1 ? Math.min(n, 50) : DRILL_TOP_DEFAULT
}

// выбранные измерения: массив / строка через запятую / пусто (null — предложенные бэком)
export function drillDims(v) {
    let a = null
    if (Array.isArray(v)) a = v
    else if (typeof v === 'string' && v.trim()) a = v.split(/[\s,;]+/)
    if (!a) return null
    const out = []
    for (const x of a) { const s = String(x || '').trim(); if (s && !out.includes(s)) out.push(s) }
    return out.length ? out : null
}

// ключ разбитой серии → значение измерения ('' — пусто, DRILL_OTHERS_KEY — «Others»)
export function drillSeriesKey(attr) {
    if (!attr || !attr.series_dim) return null
    const k = attr.series_raw_key
    return k === undefined || k === null ? '' : String(k)
}

// точка графика для запроса бэку: серия (attrOf — описание серии из attrs графика), строка графика, ось X
//   seriesBinding — binding серии wijmo (у разбитой меры 'мера::значение'), item — строка данных графика, xKey — ключ оси X в строке,
//   xBinding — поле оси X в выборке (attr.axisX_bindingX), siblings — описания серий графика (для «Others»: показанные значения той же меры)
export function drillPoint({ seriesBinding, seriesName, attr, item, xKey, xBinding, xText, color, siblings }) {
    const a = attr || {}
    const measure = a.series_binding || seriesBinding
    const key = drillSeriesKey(a)
    const pt = {
        measure,
        seriesKey: seriesBinding,
        seriesTitle: String(seriesName || a.title || seriesBinding || ''),
        dim: key === null ? '' : String(a.series_dim || ''),
        key: key === null ? null : key,
        others: [],
        xBinding: String(xBinding || xKey || ''),
        xValue: item ? item[xKey] : undefined,
        xText: xText || '',
        value: item ? drillNum(item[seriesBinding]) : null,
        color: color || '',
        aggregate: String(a.aggregate || ''),
    }
    if (pt.dim && key === DRILL_OTHERS_KEY) {
        for (const s of siblings || []) {
            if (!s || s.series_binding !== measure || s.series_dim !== pt.dim) continue
            const k = drillSeriesKey(s)
            if (k !== null && k !== DRILL_OTHERS_KEY && !pt.others.includes(k)) pt.others.push(k)
        }
    }
    return pt
}

// параметры метода бэка chart_drill
export function drillParams(pt, { dims, top, more } = {}) {
    const p = { measure: pt.measure, x: { binding: pt.xBinding, value: pt.xValue }, top: drillTop(top) }
    if (more !== undefined) p.more = more
    const d = drillDims(dims)
    if (d) p.dims = d
    if (pt.dim) p.series = { dim: pt.dim, key: pt.key, others: pt.others || [] }
    return p
}

// цвет серии для образца / полос формы: css-цвет или css-градиент как есть; градиент wijmo 'l(x1, y1, x2, y2)cadetblue-cadetblue:1:0.5' — первый цвет
export function drillCssColor(c) {
    const s = String(c || '').trim()
    if (!s) return ''
    if (/^[lr]\(/.test(s)) {
        const first = s.replace(/^[lr]\([^)]*\)/, '').split('-')[0].split(':')[0].trim()
        return /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([^)]*\))$/.test(first) ? first : ''
    }
    return s
}

export function drillNum(v) {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

// число для формы: целые при |v| ≥ 100, иначе до 2 знаков; неразрывные пробелы групп (числа не рвутся)
export function drillFmt(v, locale) {
    const n = drillNum(v)
    if (n === null) return '—'
    const digits = Math.abs(n) >= 100 ? 0 : Math.abs(n) >= 1 ? 2 : 3
    let s
    try { s = new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n) } catch (e) { s = String(Math.round(n * 1000) / 1000) }
    return s.replace(/\s/g, '\u00a0')
}

export function drillPct(share, locale) {
    if (share === null || share === undefined || !Number.isFinite(share)) return ''
    const p = share * 100
    const digits = Math.abs(p) >= 10 ? 0 : 1
    let s
    try { s = new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: digits }).format(p) } catch (e) { s = String(Math.round(p)) }
    return s.replace(/\s/g, '\u00a0') + '%'
}

// строки блока измерения: доля от значения точки (только у аддитивной меры с одним знаком частей), полоса — от наибольшего |значения| блока
export function drillBlockRows(dim, { total, additive, locale, emptyTitle } = {}) {
    const rows = (dim && dim.rows) || []
    const more = (dim && dim.more) || []
    const all = rows.concat(more)
    const others = dim && dim.others
    const vals = all.map(r => drillNum(r.value)).filter(v => v !== null)
    if (others && drillNum(others.value) !== null) vals.push(drillNum(others.value))
    const maxAbs = vals.reduce((m, v) => Math.max(m, Math.abs(v)), 0) || 1
    const sameSign = vals.every(v => v >= 0) || vals.every(v => v <= 0)
    const t = drillNum(total)
    const shareOn = !!additive && sameSign && t !== null && t !== 0
    const conv = (r) => {
        const v = drillNum(r.value)
        return {
            key: r.key, title: r.key === '' ? (emptyTitle || '(empty)') : (r.title || r.key), segment: r.segment || '',
            value: v, valueText: drillFmt(v, locale), neg: v !== null && v < 0,
            share: shareOn && v !== null ? v / t : null, shareText: shareOn && v !== null ? drillPct(v / t, locale) : '',
            barPct: v === null ? 0 : Math.max(2, Math.round(Math.abs(v) / maxAbs * 100)),
        }
    }
    const out = { rows: rows.map(conv), more: more.map(conv), others: null, shareOn }
    if (others) {
        const v = drillNum(others.value)
        out.others = { count: others.count || 0, value: v, valueText: v === null ? '' : drillFmt(v, locale), neg: v !== null && v < 0,
            shareText: shareOn && v !== null ? drillPct(v / t, locale) : '', barPct: v === null ? 0 : Math.max(2, Math.round(Math.abs(v) / maxAbs * 100)),
            moreShown: more.length }
    }
    return out
}

// сумма строк и «прочих» блока (проверка: у суммы = значению точки)
export function drillBlockSum(dim) {
    let s = 0
    for (const r of (dim && dim.rows) || []) s += drillNum(r.value) || 0
    if (dim && dim.others) s += drillNum(dim.others.value) || 0
    return s
}

// ---------------------------------------------------------------- разбиение серии в записи виджета
// узел поля в дереве viewList (attrs): { node, list, index } | null
export function drillFindNode(list, binding) {
    if (!Array.isArray(list)) return null
    for (let i = 0; i < list.length; i++) {
        const n = list[i]
        if (!n || typeof n !== 'object') continue
        if (n.binding === binding) return { node: n, list, index: i }
        const sub = drillFindNode(n.attrs, binding)
        if (sub) return sub
    }
    return null
}

// разбить меру measure по измерению dim (top значений + «Others»): мера получает series_dimension / series_limit, колонка измерения — в поля
// виджета (после поля оси X, чтобы ось не сменилась), use = true, chart_hidden = true. Возвращает снимок для отката (undo) либо null (меры нет)
export function drillSplitApply(doc, { measure, dim, dimField, top, xBinding }) {
    if (!doc || !measure || !dim) return null
    const vl = Array.isArray(doc.viewList) ? doc.viewList : (doc.viewList = [])
    const m = drillFindNode(vl, measure)
    if (!m) return null
    const undo = {
        measure, dim,
        prevDim: Object.prototype.hasOwnProperty.call(m.node, 'series_dimension') ? m.node.series_dimension : undefined,
        prevLimit: Object.prototype.hasOwnProperty.call(m.node, 'series_limit') ? m.node.series_limit : undefined,
        // серии разреза бэк дописывает в узел меры (node.series) и узел series_group в поля — при откате снять, если их не было
        prevSeries: Object.prototype.hasOwnProperty.call(m.node, 'series') ? JSON.parse(JSON.stringify(m.node.series)) : undefined,
        hadSeriesGroup: vl.some(n => n && n.name === 'series_group'),
        dimAdded: false, dimPrev: null,
    }
    m.node.series_dimension = dim
    m.node.series_limit = drillTop(top)
    const d = drillFindNode(vl, dim)
    if (d) {
        undo.dimPrev = { use: d.node.use, chart_hidden: d.node.chart_hidden }
        d.node.use = true
        if (d.node.chart_hidden === undefined) d.node.chart_hidden = true
    } else {
        const f = { binding: dim, title: (dimField && dimField.title) || dim, attr_type: 'AttrLink', relation_segment: (dimField && dimField.relation_segment) || '',
            use: true, chart_hidden: true }
        let ix = vl.findIndex(n => n && n.binding === xBinding)
        if (ix < 0) ix = vl.findIndex(n => n && n.attr_type === 'AttrDate')
        vl.splice(ix >= 0 ? ix + 1 : 0, 0, f)
        undo.dimAdded = true
    }
    return undo
}

// откат разбиения по снимку drillSplitApply
export function drillSplitUndo(doc, undo) {
    if (!doc || !undo) return false
    const vl = Array.isArray(doc.viewList) ? doc.viewList : []
    const m = drillFindNode(vl, undo.measure)
    if (m) {
        if (undo.prevDim === undefined) delete m.node.series_dimension; else m.node.series_dimension = undo.prevDim
        if (undo.prevLimit === undefined) delete m.node.series_limit; else m.node.series_limit = undo.prevLimit
        if (undo.prevSeries === undefined) delete m.node.series; else m.node.series = undo.prevSeries
    }
    if (undo.hadSeriesGroup === false) {
        for (let i = vl.length - 1; i >= 0; i--) if (vl[i] && vl[i].name === 'series_group') vl.splice(i, 1)
    }
    const d = drillFindNode(vl, undo.dim)
    if (d) {
        if (undo.dimAdded) d.list.splice(d.index, 1)
        else if (undo.dimPrev) {
            if (undo.dimPrev.use === undefined) delete d.node.use; else d.node.use = undo.dimPrev.use
            if (undo.dimPrev.chart_hidden === undefined) delete d.node.chart_hidden; else d.node.chart_hidden = undo.dimPrev.chart_hidden
        }
    }
    return true
}

// снять разбиение без снимка (разрез пришёл сохранённым в записи): у меры убираются series_dimension / series_limit; колонка измерения остаётся
export function drillSplitRemove(doc, measure) {
    const m = drillFindNode(doc && doc.viewList, measure)
    if (!m) return false
    delete m.node.series_dimension
    delete m.node.series_limit
    return true
}

// сводка настроек для свёрнутого блока: «Product, Brand · top 5 · suggested»
export function drillSettingsSummary(cands, top, custom, tr) {
    const t_ = tr || ((s) => s)
    const on = (cands || []).filter(c => c && c.on).map(c => c.title || c.binding)
    return [on.join(', ') || '—', t_('top') + ' ' + drillTop(top), custom ? t_('custom') : t_('suggested')].join(' · ')
}
